import { useEffect, useState } from 'react';
import type { ArchiveRecord, UpdateMetadataRequest } from '../types/moderation';
import { ApiError, updateMetadata } from '../lib/api';
import { toast } from '../lib/toast';

interface InternalNotesEditorProps {
  record: ArchiveRecord;
  onSaved: (record: ArchiveRecord) => void;
  /**
   * Render the legacy "field-internalNotes" testid (Notes tab) or
   * "field-internalNotes-main" testid (Details tab inline). Existing
   * tests still target both ids; once F-203 lands and MetadataEditor
   * stops also rendering an internalNotes field on the Details tab the
   * `-main` variant can be retired.
   */
  testIdSuffix?: 'main' | 'notes';
}

/**
 * F-102: focused editor for the `internalNotes` field only.
 *
 * Previously this textarea was rendered by the giant MetadataEditor
 * component via a `section` prop. Two instances (one on the Details
 * tab, one on the Notes tab) had independent form state, which created
 * a silent data-loss path when the user typed in one tab and the other
 * was holding stale data.
 *
 * This component owns its own initial / form state but reads only the
 * `internalNotes` field so the two tabs always send the same payload.
 */
export default function InternalNotesEditor({
  record,
  onSaved,
  testIdSuffix = 'notes',
}: InternalNotesEditorProps) {
  const [value, setValue] = useState(record.internalNotes || '');
  const [initial, setInitial] = useState(record.internalNotes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(record.internalNotes || '');
    setInitial(record.internalNotes || '');
  }, [record]);

  const dirty = value !== initial;
  const testId =
    testIdSuffix === 'main' ? 'field-internalNotes-main' : 'field-internalNotes';

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: Partial<UpdateMetadataRequest> = { internalNotes: value };
      const saved = await updateMetadata(record.submissionId, payload, record.updatedAt);
      onSaved(saved);
      setInitial(saved.internalNotes || '');
      toast.success('Notes saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const conflict = err.body as { record?: ArchiveRecord } | null;
        if (conflict?.record) {
          onSaved(conflict.record);
        }
        setError('Someone else updated this submission; the notes have been refreshed.');
      } else {
        setError((err as Error).message || 'Save failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => setValue(initial);

  return (
    <div className="metadata-editor" data-testid="metadata-editor-notes">
      <label className="form-group">
        <span>Internal notes</span>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={6}
          data-testid={testId}
        />
      </label>
      {error && (
        <div className="field-error" role="alert">
          {error}
        </div>
      )}
      <div className="editor-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={cancel}
          disabled={!dirty || busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={!dirty || busy}
          data-testid="metadata-save"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
